/* Muletta cart recommendations v2: size first, mixed-size cart uses recommendation rank. Uses the existing theme's bundled cart sections.
 * The discount itself must be created in Shopify; this asset never changes prices.
 */
(() => {
  if (customElements.get('muletta-cart-upsell')) return;
  const normalize = value => String(value || '').trim().toLowerCase();
  const isSize = name => ['size', 'talla', 'tamaño', 'tamano'].includes(normalize(name));
  const optionsOf = product => (product.options || []).map(option =>
    typeof option === 'string' ? option : option.name);
  const valuesOf = variant => variant.options || [variant.option1, variant.option2, variant.option3].filter(v => v != null);
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  let adding = false;

  class MulettaCartUpsell extends HTMLElement {
    connectedCallback() {
      // Custom elements reconnect automatically after the theme replaces drawer content.
      this.controller?.abort();
      this.controller = new AbortController();
      this.load(this.controller.signal).catch(() => { /* Keep checkout usable if recommendations fail. */ });
    }

    disconnectedCallback() { this.controller?.abort(); }

    async load(signal) {
      this.root = window.Shopify?.routes?.root || '/';
      this.seeds = [...this.querySelectorAll('[data-seed]')].map(node => ({
        id: node.dataset.id, type: node.dataset.type, size: node.dataset.size
      }));
      const cartIDs = new Set(this.seeds.map(seed => String(seed.id)));
      const ids = [...cartIDs];
      const results = [];
      // Include each cart product, with at most three recommendation requests at once.
      for (let offset = 0; offset < ids.length; offset += 3) {
        if (signal.aborted) return;
        results.push(...await Promise.allSettled(ids.slice(offset, offset + 3).map(async id => {
        const url = new URL(`${this.root}recommendations/products.json`, location.origin);
        url.search = new URLSearchParams({ product_id: id, intent: 'related', limit: '10' });
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data.products) ? data.products : [];
        })));
      }
      if (signal.aborted || !this.isConnected) return;
      const groups = results.map(result => result.status === 'fulfilled' ? result.value : []);
      const candidates = new Map();
      // Combine Shopify's ranked lists. Products recommended for several cart
      // products receive more weight; this score is not a raw sales count.
      for (const group of groups) {
        const counted = new Set();
        group.forEach((product, rank) => {
          const id = String(product.id);
          if (cartIDs.has(id) || counted.has(id) || !product.available) return;
          counted.add(id);
          const candidate = candidates.get(id) || { product, score: 0 };
          candidate.score += 1 / (rank + 1);
          candidates.set(id, candidate);
        });
      }
      const cards = [];
      for (const { product } of [...candidates.values()].sort((a, b) => b.score - a.score)) {
        const card = this.card(product);
        if (card) cards.push(card);
        if (cards.length === 2) break;
      }
      const list = this.querySelector('[data-upsell-list]');
      list.replaceChildren(...cards);
      this.hidden = cards.length === 0;
    }

    card(product) {
      const variants = (product.variants || []).filter(variant => variant.available && !variant.requires_selling_plan);
      if (!variants.length || product.requires_selling_plan) return null;
      const names = optionsOf(product);
      const sizeIndex = names.findIndex(isSize);
      // Size is a cart-wide priority, regardless of product-type labels.
      // Non-sized cart items do not erase an otherwise unambiguous size.
      const sizes = new Set(this.seeds.map(seed => normalize(seed.size)).filter(Boolean));
      const size = sizes.size === 1 ? [...sizes][0] : null;
      const matched = size && sizeIndex >= 0
        ? variants.filter(variant => normalize(valuesOf(variant)[sizeIndex]) === size)
        : [];
      // With one cart size, never fill a slot with a product lacking that size.
      // Mixed/unknown cart sizes keep Shopify's product ranking and ask for a size.
      if (size && matched.length === 0) return null;
      let selected = matched[0] || (variants.length === 1 && sizeIndex < 0 ? variants[0] : null);
      const card = element('article', 'muletta-upsell__card');
      const productURL = new URL(product.url || `${this.root}products/${product.handle}`, location.origin);
      if (productURL.origin !== location.origin) return null;
      const photoLink = element('a', 'muletta-upsell__photo');
      photoLink.href = productURL.href;
      const photo = element('img');
      photo.alt = product.title;
      photo.loading = 'lazy';
      photo.width = 72;
      photo.height = 90;
      const setPhoto = variant => {
        const source = variant?.featured_image?.src || product.featured_image?.src || product.featured_image || product.images?.[0];
        if (typeof source !== 'string') return;
        const url = new URL(source, location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return;
        url.searchParams.set('width', '160');
        photo.src = url.href;
      };
      photoLink.append(photo);
      const info = element('div', 'muletta-upsell__info');
      const title = element('a', 'muletta-upsell__title', product.title);
      title.href = productURL.href;
      const price = element('p', 'muletta-upsell__price');
      const currency = this.dataset.currency || 'MXN';
      const money = amount => new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(amount) / 100) + ' ' + currency;
      const form = element('form', 'muletta-upsell__form');
      const select = element('select', 'muletta-upsell__select');
      select.setAttribute('aria-label', `Elige talla y opción para ${product.title}`);
      select.required = true;
      const placeholder = element('option', '', sizeIndex >= 0 ? 'Elige tu talla / opción' : 'Elige una opción');
      placeholder.value = '';
      placeholder.disabled = true;
      select.append(placeholder);
      for (const variant of variants) {
        const values = valuesOf(variant);
        const label = names.map((name, index) => `${name}: ${values[index] || ''}`).join(' · ');
        const option = element('option', '', variant.title === 'Default Title' ? 'Única opción' : label || variant.title);
        option.value = String(variant.id);
        select.append(option);
      }
      select.value = selected ? String(selected.id) : '';
      const button = element('button', 'button muletta-upsell__add', 'Agregar');
      button.type = 'submit';
      button.disabled = !selected;
      const error = element('p', 'muletta-upsell__error');
      error.setAttribute('role', 'status');
      error.hidden = true;
      const update = () => {
        selected = variants.find(variant => String(variant.id) === select.value) || null;
        price.textContent = selected ? money(selected.price) : `Desde ${money(Math.min(...variants.map(variant => variant.price)))}`;
        const url = new URL(productURL);
        if (selected) url.searchParams.set('variant', String(selected.id));
        title.href = photoLink.href = url.href;
        setPhoto(selected);
        button.disabled = !selected;
        error.hidden = true;
      };
      select.addEventListener('change', update);
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (selected && !adding) this.add(selected, button, select, error);
      });
      update();
      form.append(select, button);
      info.append(title, price, form, error);
      card.append(photoLink, info);
      return card;
    }

    async add(variant, button, select, error) {
      if (adding) return;
      adding = true;
      button.disabled = select.disabled = true;
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;
      const sections = [this.dataset.sectionId];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const requestedSections = [...new Set(sections.filter(Boolean))].slice(0, 5);
      let added = false;
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: requestedSections, sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'No se pudo agregar esta prenda. Inténtalo de nuevo.');
        added = true;
        const cartResponse = await fetch(`${this.root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('refresh');
        const cart = await cartResponse.json();
        cart.sections = result.sections || {};
        // Null sections can accompany a successful add; refresh instead of retrying the add.
        if (requestedSections.some(id => !cart.sections[id])) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true, detail: { items: result.items || [result], cart, onSuccessDo: 'force_open_drawer' }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
          }));
        }
        button.textContent = 'Agregado';
      } catch (problem) {
        if (added) {
          error.textContent = 'Prenda agregada. Actualiza la página para ver tu cesta.';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          error.textContent = problem instanceof TypeError
            ? 'No pudimos confirmar el cambio. Revisa tu cesta antes de intentar de nuevo.'
            : problem.message;
        }
        error.hidden = false;
      } finally {
        adding = false;
        button.removeAttribute('aria-busy');
        // After a successful add, wait for the theme to replace this card.
        button.disabled = select.disabled = added;
      }
    }
  }
  customElements.define('muletta-cart-upsell', MulettaCartUpsell);
})();